import { normalizeUri } from "micromark-util-sanitize-uri";

export type CitationEntry = string | Record<string, string>;

export type CitationMarkerStyle = "direct" | "legacy_numeric" | "none";

export function citationEntryKey(entry: CitationEntry): string {
  if (typeof entry === "string") return entry;
  return Object.keys(entry)[0] ?? "";
}

export function citationEntryContent(entry: CitationEntry): string {
  if (typeof entry === "string") return entry;
  const key = citationEntryKey(entry);
  return entry[key] ?? "";
}

export function decodeCitationKey(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  try {
    const decoded = decodeURIComponent(trimmed);
    if (decoded === trimmed) return trimmed;
    // Decode react-markdown / micromark href encoding, but keep verbatim %XX in
    // EXACT_KEY names (e.g. Report%202024) when re-encoding would not round-trip.
    // Micromark percent-encodes spaces but leaves `+` intact; encodeURIComponent
    // turns `+` into `%2B`, so treat those forms as equivalent for the check.
    const reencoded = encodeURIComponent(decoded);
    if (reencoded === trimmed) return decoded;
    if (reencoded.replace(/%2B/gi, "+") === trimmed) return decoded;
    return trimmed;
  } catch {
    return trimmed;
  }
}

/**
 * Resolve a react-markdown / micromark citation href payload to a stored key.
 * Prefer matching known EXACT_KEY values via micromark normalizeUri (comma,
 * colon, +, and literal-%XX keys); fall back to decodeCitationKey for labels.
 */
export function resolveCitationKeyFromHref(
  rawHref: string,
  knownKeys?: Iterable<string>,
): string {
  const trimmed = rawHref.trim();
  if (!trimmed) return "";

  if (knownKeys) {
    const normalizedHref = normalizeUri(trimmed);
    for (const key of knownKeys) {
      if (!key) continue;
      if (key === trimmed) return key;
      const normalizedKey = normalizeUri(key);
      if (normalizedKey === trimmed || normalizedKey === normalizedHref) {
        return key;
      }
    }
  }

  return decodeCitationKey(trimmed);
}

/** True when the citation: payload is a pure 1-based index from the old format. */
export function isLegacyNumericCitationToken(token: string): boolean {
  return /^\d+$/.test(token.trim());
}

/**
 * Detect marker style from answer content.
 *
 * Dual-mode exists because historical messages used citation:N (and bare [N]
 * that normalize into citation:N). New answers use citation:KEY. We do not
 * canonicalize legacy→keys at ingestion; both paths stay until those messages age out.
 *
 * Angle-bracket markers (`](<citation:...>`) are always direct, including digit-only
 * keys such as a document titled "2024". Plain non-numeric `](citation:KEY)` also
 * counts as direct (model may omit <>). Legacy wins only when no direct markers exist.
 *
 * Bare [N] alone must not force legacy when direct keys are present — prose like
 * "option [1]", "Table [2]", or "[2024]" is not a citation marker.
 *
 * Follow-ups (ENG-1767 review): API `content` may include raw citation markers for
 * new staff answers; confirm Connect/API consumers before prod. ENG-1725 (Answer
 * Reasoning) must adopt `citation:open` detail.key (not index) when it merges.
 * Translations may pass optional `contentForOrdering` on the event so opens resolve
 * against the surface that rendered the badge.
 */
export function detectCitationMarkerStyle(
  content: string,
  citationLabels?: Iterable<string>,
): CitationMarkerStyle {
  const labels = citationLabels ? new Set(citationLabels) : null;
  const hasAngleDirect = /\]\(\s*<citation:[^>\n]+>\s*\)/.test(content);
  // Allow one nesting level of parentheses in non-angle keys (e.g. Doc (2024)).
  const hasPlainDirect =
    /\]\(\s*citation:(?!\d+\s*\))((?:[^()\n]|\([^()\n]*\))+)\s*\)/.test(content);
  if (hasAngleDirect || hasPlainDirect) return "direct";

  if (labels && hasLabelMatchedPlainDigitDirect(content, labels)) return "direct";

  // Plain inventeds like [cite](citation:7) are direct (unresolved), including when
  // classic [N](citation:N) leftovers share the message. Check before legacy.
  if (hasPlainInventedDigitDirect(content)) return "direct";

  // Classic legacy uses a numeric markdown label: [N](citation:N).
  const hasClassicLegacyLink = /\[\d+\]\(\s*<?citation:\d+>?\s*\)/.test(content);
  if (hasClassicLegacyLink) return "legacy_numeric";

  // Historical answers sometimes shipped bare [N] without citation: links yet.
  if (/\[(\d+)\](?!\()/.test(content)) return "legacy_numeric";

  return "none";
}

function citationLabelsFromList(list: CitationEntry[]): Set<string> {
  return new Set(list.map(citationEntryKey).filter(Boolean));
}

function hasLabelMatchedPlainDigitDirect(content: string, labels: Set<string>): boolean {
  if (labels.size === 0) return false;
  for (const match of content.matchAll(/\]\(\s*citation:(\d+)\s*\)/g)) {
    const key = (match[1] || "").trim();
    if (key && labels.has(key)) return true;
  }
  return false;
}

/** True for plain digit hrefs whose link text is not classic [N], e.g. [cite](citation:7). */
function hasPlainInventedDigitDirect(content: string): boolean {
  for (const match of content.matchAll(/\[([^\]]+)\]\(\s*citation:(\d+)\s*\)/g)) {
    const label = (match[1] || "").trim();
    if (label && !isLegacyNumericCitationToken(label)) return true;
  }
  return false;
}

function contentHasDirectCitationMarkers(
  content: string,
  labels?: Set<string>,
): boolean {
  if (
    /\]\(\s*<citation:[^>\n]+>\s*\)/.test(content) ||
    /\]\(\s*citation:(?!\d+\s*\))((?:[^()\n]|\([^()\n]*\))+)\s*\)/.test(content) ||
    hasPlainInventedDigitDirect(content)
  ) {
    return true;
  }
  return labels != null && hasLabelMatchedPlainDigitDirect(content, labels);
}

/** Extract direct citation keys from answer markdown in first-appearance order. */
export function extractCitedKeys(
  content: string,
  citationLabels?: Iterable<string>,
): string[] {
  const keys: string[] = [];
  const seen = new Set<string>();
  const labels = citationLabels ? new Set(citationLabels) : null;
  const hasDirectMarkers = contentHasDirectCitationMarkers(
    content,
    labels ?? undefined,
  );

  type CitationLinkMatch = {
    index: number;
    key: string;
    isAngleBracket: boolean;
    linkText: string;
  };
  const matches: CitationLinkMatch[] = [];

  for (const match of content.matchAll(/\[([^\]]*)\]\(\s*<citation:([^>\n]+)>\s*\)/g)) {
    matches.push({
      index: match.index ?? 0,
      key: (match[2] || "").trim(),
      isAngleBracket: true,
      linkText: (match[1] || "").trim(),
    });
  }
  // One nesting level of parentheses so "Doc (2024) - Item 3" is not truncated.
  for (const match of content.matchAll(
    /\[([^\]]+)\]\(\s*citation:((?:[^()\n]|\([^()\n]*\))+)\s*\)/g,
  )) {
    matches.push({
      index: match.index ?? 0,
      key: (match[2] || "").trim(),
      isAngleBracket: false,
      linkText: (match[1] || "").trim(),
    });
  }

  matches.sort((a, b) => a.index - b.index);

  for (const { key, isAngleBracket, linkText } of matches) {
    // Mixed messages: classic [N](citation:N) is legacy index syntax, not key "N".
    // Keep plain inventeds such as [cite](citation:7) so badges stay unresolved.
    if (
      hasDirectMarkers &&
      !isAngleBracket &&
      isLegacyNumericCitationToken(key) &&
      !labels?.has(key) &&
      isLegacyNumericCitationToken(linkText)
    ) {
      continue;
    }
    if (!key || seen.has(key)) continue;
    seen.add(key);
    keys.push(key);
  }

  return keys;
}

/** Extract legacy 1-based citation indices in first-appearance order. */
export function extractLegacyCitedIndices(content: string): number[] {
  const indices: number[] = [];
  const seen = new Set<number>();

  const consider = (raw: string) => {
    const index = Number(raw);
    if (!Number.isFinite(index) || index < 1 || seen.has(index)) return;
    seen.add(index);
    indices.push(index);
  };

  for (const match of content.matchAll(/\]\(\s*<?citation:(\d+)>?\s*\)/g)) {
    consider(match[1] || "");
  }
  for (const match of content.matchAll(/\[(\d+)\](?!\()/g)) {
    consider(match[1] || "");
  }

  return indices;
}

export type OrderedCitation = {
  entry: CitationEntry;
  key: string;
  cited: boolean;
  /** Original 1-based index in meta_data.citation (useful for legacy open events). */
  originalIndex: number;
};

function orderLegacyNumericCitations(
  list: CitationEntry[],
  content: string,
): OrderedCitation[] {
  const citedIndices = new Set(extractLegacyCitedIndices(content));

  return list.map((entry, index) => {
    const originalIndex = index + 1;
    return {
      entry,
      key: citationEntryKey(entry) || `legacy-${originalIndex}`,
      cited: citedIndices.has(originalIndex),
      originalIndex,
    };
  });
}

function directEntryKey(entry: CitationEntry, index: number): string {
  return citationEntryKey(entry) || `legacy-${index + 1}`;
}

function orderDirectCitations(
  list: CitationEntry[],
  content: string,
): OrderedCitation[] {
  const labels = citationLabelsFromList(list);
  const citedKeys = extractCitedKeys(content, labels);
  const ordered: OrderedCitation[] = [];
  const usedIndexes = new Set<number>();

  // Keep every meta_data.citation entry (duplicate keys keep distinct chunk bodies).
  for (const citedKey of citedKeys) {
    list.forEach((entry, index) => {
      if (usedIndexes.has(index)) return;
      const originalIndex = index + 1;
      const key = directEntryKey(entry, index);
      if (key !== citedKey) return;
      usedIndexes.add(index);
      ordered.push({
        entry,
        key,
        cited: true,
        originalIndex,
      });
    });
  }

  list.forEach((entry, index) => {
    if (usedIndexes.has(index)) return;
    const originalIndex = index + 1;
    const key = directEntryKey(entry, index);
    usedIndexes.add(index);
    ordered.push({
      entry,
      key,
      cited: false,
      originalIndex,
    });
  });

  return ordered;
}

/**
 * Order + cite/consult flags for the References panel.
 * Legacy numeric messages keep stored list order and index-based blue/gray.
 * Direct-key messages reorder by first appearance, then consulted-only.
 */
export function orderCitationsByAppearance(
  citations: CitationEntry[] | undefined,
  content: string,
): OrderedCitation[] {
  const list = Array.isArray(citations) ? citations : [];
  if (list.length === 0) return [];

  const labels = citationLabelsFromList(list);
  const style = detectCitationMarkerStyle(content, labels);
  if (style === "legacy_numeric") {
    return orderLegacyNumericCitations(list, content);
  }

  return orderDirectCitations(list, content);
}

/**
 * Resolve an open-event token to a citation entry.
 * Tokens may be stored keys or micromark href payloads from inline badges.
 */
export function resolveCitationOpenTarget(
  citations: CitationEntry[] | undefined,
  content: string,
  rawToken: string,
  originalIndex?: number,
): OrderedCitation | undefined {
  const token = rawToken.trim();
  if (!token && originalIndex == null) return undefined;

  const ordered = orderCitationsByAppearance(citations, content);
  if (ordered.length === 0) return undefined;

  if (
    originalIndex != null &&
    Number.isFinite(originalIndex) &&
    originalIndex >= 1
  ) {
    const byIndex = ordered.find((item) => item.originalIndex === originalIndex);
    if (byIndex) return byIndex;
  }

  if (!token) return undefined;

  // Legacy open events still send the original 1-based index as a digit string.
  // Direct open events send the exact key (including digit-only document titles).
  const list = Array.isArray(citations) ? citations : [];
  const labels = citationLabelsFromList(list);

  if (
    isLegacyNumericCitationToken(token) &&
    detectCitationMarkerStyle(content, labels) === "legacy_numeric"
  ) {
    const index = Number(token);
    return ordered.find((item) => item.originalIndex === index);
  }

  // Inline citation:open carries key only; duplicate keys resolve to the first ordered match.
  // Answer Reasoning and References rows pass originalIndex to open the exact chunk.
  // Micromark href payloads may not equal the stored key; normalize both sides.
  const resolvedKey = resolveCitationKeyFromHref(
    token,
    ordered.map((item) => item.key),
  );
  return ordered.find((item) => item.key === resolvedKey);
}

/**
 * Map citation keys (and legacy numeric tokens) to the 1-based number shown in
 * the References list, so inline badges match that ordering.
 */
export function buildCitationDisplayNumbers(
  citations: CitationEntry[] | undefined,
  content: string,
): Map<string, number> {
  const list = Array.isArray(citations) ? citations : [];
  const labels = citationLabelsFromList(list);
  const ordered = orderCitationsByAppearance(citations, content);
  const style = detectCitationMarkerStyle(content, labels);
  const numbers = new Map<string, number>();

  ordered.forEach((item, index) => {
    const displayNumber = index + 1;
    // First appearance wins when duplicate keys share a badge number.
    if (!numbers.has(item.key)) {
      numbers.set(item.key, displayNumber);
    }
    // Legacy markers use citation:N where N is the original stored index.
    if (style === "legacy_numeric") {
      numbers.set(String(item.originalIndex), displayNumber);
    }
  });

  return numbers;
}

/**
 * Upgrade non-angle direct markers so react-markdown parses keys with spaces.
 * Allow one nesting level of parentheses in keys (e.g. "Doc (2024) - Item 3").
 */
function isLegacyNumericLinkKey(key: string, labels?: Set<string>): boolean {
  return isLegacyNumericCitationToken(key) && !labels?.has(key);
}

/**
 * Remove classic legacy numeric citation links when direct keys are present.
 * Only strip digits-only markdown labels (`[N](citation:N)` / `[N](<citation:N>)`).
 * Keep direct angled keys such as `[cite](<citation:99>)` so invented digit keys
 * can still render as unresolved badges.
 */
function stripLegacyNumericCitationLinks(
  content: string,
  labels?: Set<string>,
): string {
  return content.replace(
    /\[(\d+)\]\(\s*<?citation:(\d+)>?\s*\)/g,
    (match, label: string, digits: string) => {
      const key = (digits || "").trim();
      if (!isLegacyNumericLinkKey(key, labels)) return match;
      return `[${label}]`;
    },
  );
}

function ensureAngleBracketCitationLinks(
  content: string,
  labels?: Set<string>,
  skipLegacyNumeric = false,
): string {
  return content.replace(
    /\[([^\]]+)\]\(\s*citation:((?:[^()\n]|\([^()\n]*\))+)\s*\)/g,
    (match, label: string, rawKey: string) => {
      const key = rawKey.trim().replace(/^<|>$/g, "");
      // Leave classic [N](citation:N) alone when stripping legacy leftovers; still
      // upgrade plain inventeds like [cite](citation:7) to angled direct form.
      if (
        skipLegacyNumeric &&
        isLegacyNumericLinkKey(key, labels) &&
        isLegacyNumericCitationToken(label.trim())
      ) {
        return match;
      }
      return `[${label}](<citation:${key}>)`;
    },
  );
}

/** Normalize both legacy numeric markers and direct-key markers for display. */
export function normalizeCitationLinks(
  content: string,
  citations?: CitationEntry[],
): string {
  const labels =
    citations && citations.length > 0 ? citationLabelsFromList(citations) : undefined;
  const style = detectCitationMarkerStyle(content, labels);

  if (style === "legacy_numeric") {
    return content
      .replace(/([.?!])\s*\[(\d+)\]\(citation:(\d+)\)/g, "$1 [$2](citation:$3)")
      .replace(/\[(\d+)\]\(citation:(\d+)\)\s*([.?!])/g, "$3 [$1](citation:$2)")
      .replace(/\[(\d+)\]\s*([.?!])/g, "$2 [$1](citation:$1)")
      .replace(/([.?!])\s*\[(\d+)\](?!\()/g, "$1 [$2](citation:$2)")
      .replace(/\[(\d+)\](?!\()/g, "[$1](citation:$1)");
  }

  if (style !== "direct") return content;

  const labelSet = labels ?? undefined;
  const hasDirectMarkers = contentHasDirectCitationMarkers(content, labelSet);
  const stripped = hasDirectMarkers
    ? stripLegacyNumericCitationLinks(content, labelSet)
    : content;
  const withAngles = ensureAngleBracketCitationLinks(
    stripped,
    labelSet,
    hasDirectMarkers,
  );

  // Allow parentheses inside citation keys; avoid introducing a second period
  // when punctuation was already present before the link.
  const citationLink = String.raw`\[[^\[\]]+\]\(\s*<?citation:[^>\n]+>?\s*\)`;

  return withAngles
    // Leftover ] first so trailing punctuation can still move before the badge.
    .replace(new RegExp(`(?<!\\[)(${citationLink})\\]`, "g"), "$1")
    .replace(new RegExp(`([.?!])\\s*(${citationLink})`, "g"), "$1 $2")
    .replace(
      // Move sentence punctuation before citation stacks anywhere in the answer
      // (not only at end-of-string), including mid-content multi-sentence cases.
      new RegExp(`(\\s*)((?:${citationLink})+)\\s*([.?!]+)`, "g"),
      (match, leadingSpace, fullStack, punct, offset, str) => {
        const proseBeforeStack = str.slice(0, offset).trimEnd();
        if (/[.?!]$/.test(proseBeforeStack)) return `${leadingSpace}${fullStack}`;
        return `${punct} ${fullStack}`;
      },
    );
}

/**
 * Translation cleanup. Direct-style answers skip the glued-digit legacy repair
 * (e.g. artículo3. must not become [3](citation:3)). Legacy/none still repair.
 */
export function normalizeTranslationCitationLinks(
  content: string,
  maxCitationIndex = 0,
  citations?: CitationEntry[],
): string {
  const labels =
    citations && citations.length > 0 ? citationLabelsFromList(citations) : undefined;
  if (detectCitationMarkerStyle(content, labels) === "direct") {
    return normalizeCitationLinks(content, citations);
  }

  let updated = content;
  if (Number.isFinite(maxCitationIndex) && maxCitationIndex >= 1) {
    updated = updated.replace(
      /([A-Za-z\u00C0-\u024F])(\d{1,3})([.?!])/g,
      (match, letter, digits, punctuation) => {
        const index = Number(digits);
        if (!Number.isFinite(index) || index < 1 || index > maxCitationIndex) return match;
        return `${letter}${punctuation} [${index}](citation:${index})`;
      },
    );
  }
  return normalizeCitationLinks(updated, citations);
}
